import { QueryBuilder } from "@repo/helpers";
import { isBefore, subHours } from "date-fns";

import type { BranchViewModel, PositionViewModel, RetailLocationViewModel } from "../../../../models/location";
import type { LayerDefinition } from "../../../../models/mapConfiguration";
import type { LayerItem } from "./layerRequests";

import { isOfficeLayer, isRetailLayer } from "../../layers/layerTypes";
import { latestPositionPerGroup, positionGroupKey } from "./positionGrouping";

export interface FilteredLayerItems {
	items: LayerItem[];
	totalCount: number;
}

const EMPTY_RESULT: FilteredLayerItems = { items: [], totalCount: 0 };

export function filterLayerItems(layer: LayerDefinition, data: LayerItem[]): FilteredLayerItems {
	if (!data.length) return EMPTY_RESULT;

	switch (layer.itemType) {
		case "Representatives":
		case "Machines":
			return filterPositions(layer, data as PositionViewModel[]);
		case "Sales":
			return limit(layer, new QueryBuilder<LayerItem>(), data);
	}
	if (isRetailLayer(layer.itemType)) return filterRetailLocations(layer, data as RetailLocationViewModel[]);
	if (isOfficeLayer(layer.itemType)) return filterOffices(layer, data as BranchViewModel[]);
	return EMPTY_RESULT;
}

function filterPositions(layer: LayerDefinition, data: PositionViewModel[]) {
	const source = layer.lastPositionOnly
		? latestPositionPerGroup(data, (position) => positionGroupKey(layer.itemType, position))
		: data;
	const query = new QueryBuilder<PositionViewModel>();

	const totalCount = query.count(source);

	if (layer.lastPositionOnly && layer.hourLimit) {
		const cutoff = subHours(new Date(), layer.hourLimit);
		query.where((item) => !isBefore(new Date(item.date), cutoff));
	}
	if (layer.lastPositions) query.take(layer.lastPositions);

	return { items: query.execute(source), totalCount };
}

function filterRetailLocations(layer: LayerDefinition, data: RetailLocationViewModel[]) {
	const query = new QueryBuilder<RetailLocationViewModel>();
	whereAnyOf(query, layer.stateIds, (item) => item.stateId);
	if (layer.partnerId) query.where((item) => item.partnerId === layer.partnerId);
	if (layer.partnerTypeId) query.where((item) => item.partnerTypeId === layer.partnerTypeId);
	whereAnyOf(query, layer.companyDivisionIds, (item) => item.companyDivisionId);
	whereAnyOf(query, layer.companyRegionIds, (item) => item.companyRegionId);
	whereAnyOf(query, layer.companyMarketIds, (item) => item.companyMarketId);
	whereAnyOf(query, layer.statusIds, (item) => item.statusId);
	whereActiveStatus(query, layer.statusId);
	return limit(layer, query, data);
}

function filterOffices(layer: LayerDefinition, data: BranchViewModel[]) {
	const query = new QueryBuilder<BranchViewModel>();
	whereActiveStatus(query, layer.statusId);
	whereAnyOf(query, layer.brandIds, (item) => item.brandId);
	whereAnyOf(query, layer.channelIds, (item) => item.channelId);
	whereAnyOf(query, layer.marketerIds, (item) => item.marketerId);
	whereAnyOf(query, layer.stateIds, (item) => item.usStateId);
	whereAnyOf(query, layer.assignedUserIds, (item) => item.assignedUserId);
	return limit(layer, query, data);
}

/** statusId 1 keeps active items, 2 keeps inactive ones, anything else keeps both. */
function whereActiveStatus<T extends { active: boolean }>(query: QueryBuilder<T>, statusId: number | undefined) {
	if (statusId > 0) query.where((item) => item.active === (statusId === 1));
}

function whereAnyOf<T>(query: QueryBuilder<T>, selected: readonly unknown[] | undefined, value: (item: T) => unknown) {
	if (selected?.length) query.where((item) => selected.includes(value(item)));
}

function limit<T extends LayerItem>(layer: LayerDefinition, query: QueryBuilder<T>, data: T[]) {
	const totalCount = query.count(data);
	if (layer.lastPositions) query.take(layer.lastPositions);
	return { items: query.execute(data), totalCount };
}
